import { CommandType } from '@open-template-hub/animated-code-editor';
import { Image } from '../image/image.model';
import { PricingOption } from '../pricing/pricing.model';
import { Review } from '../review/review.model';
import { StepTimeLineItem } from '../timeline/step-timeline.model';
import { Video } from '../video/image.model';
import {WebsiteModel} from "../website/website.model";

export interface PricingFeature {
  name: string,
  subTitle?: string,
  description?: string,
  details: { name?: string, description: string }[],
  icon?: string,
  image?: Image
}

export interface Platform {
  info: WebsiteModel;
  url: string;
  externalId?: string;
  /**
   * Rollout state of this platform listing. Defaults to `'done'` (shipped /
   * available). `'planned'` marks a listing that is on the roadmap but not yet
   * live, the coverage matrix shows it as "planned" instead of a checkmark.
   */
  status?: 'done' | 'planned';
  /**
   * Whether THIS specific platform listing is paid. Monetization is now per
   * (theme + platform), not per theme. When omitted it is derived from the
   * theme: open-source themes are free everywhere except LINE (always paid);
   * non-open-source themes are paid everywhere. Set explicitly to override
   * (e.g. all-in-one's Chrome is free while its JetBrains is paid).
   */
  paid?: boolean;
  // Each platform carries its own screenshot. The banner displays this when
  // the user picks the corresponding platform icon.
  screenshot?: Image;
  /**
   * For BUNDLE products only (see Product.isBundle): the keys of the individual
   * themes this platform's listing ships. Lets a bundle offer different theme
   * sets per platform. When omitted, the bundle falls back to: a paid platform
   * includes every theme, a free platform includes only the free themes.
   */
  bundledThemes?: string[];
  /**
   * Store SKU (`<theme>/<platform>`, e.g. 'ocean/sublime-text') when this
   * platform listing is sold as a download on codigrate.com (cart → Stripe
   * Checkout → download, see base-themes/STORE-DESIGN.md). Its price comes from
   * the store catalogue (StoreService), never from this file. A platform with a
   * sku needs no `url`: the CTA is "Add to cart" instead of a link out.
   */
  sku?: string;
  /**
   * The list price shown on the site for a listing sold here (a display
   * fallback, in the currency's smallest unit — 199 = $1.99). The Stripe Price
   * behind `sku` is what is charged; the store catalogue (StoreService)
   * overrides this the moment it answers, so the two must be kept equal when
   * the price changes. It is here so the price is on the page even before /
   * without the store API.
   */
  listPrice?: { amount: number; currency: string };
}

/** The list price of a theme's platform listing sold on the site, or undefined. */
export function platformListPrice( product: Product | undefined, platformName?: string ): { amount: number; currency: string } | undefined {
  const entry = product?.platforms?.find( p => p.info?.name === platformName );
  return entry?.sku ? entry.listPrice : undefined;
}

/** The store SKU of a theme's platform listing, or undefined when it is not sold on the site. */
export function platformSku( product: Product | undefined, platformName?: string ): string | undefined {
  const entry = product?.platforms?.find( p => p.info?.name === platformName );
  return entry?.sku || undefined;
}

/**
 * What the platform pill's CTA does, in one place for the theme page and the
 * theme/content page:
 *  - 'planned'  the listing is on the roadmap: inert "Coming soon";
 *  - 'cart'     sold on the site (Platform.sku): "Add to cart" (the mini cart opens, no page change);
 *  - 'external' a paid listing on another store (JetBrains, LINE): "Go to store" link out;
 *  - 'download' a free downloadable file outside the cart (Rhino; the terminal, Sublime and
 *               Slack listings carry a sku since 2026-09-21 and are 'cart');
 *  - 'get'      a free marketplace listing: "Install from <store>" link out.
 */
export type PlatformCtaKind = 'planned' | 'cart' | 'external' | 'download' | 'get';

export function platformCtaKind( product: Product | undefined, platformName?: string ): PlatformCtaKind {
  const entry = product?.platforms?.find( p => p.info?.name === platformName );
  if ( !entry ) return 'get';
  if ( entry.status === 'planned' ) return 'planned';
  if ( entry.sku ) return 'cart';
  if ( entry.paid ) return 'external';
  if ( entry.info?.downloadable ) return 'download';
  return 'get';
}

/**
 * Per-(theme + platform) monetization. Returns whether the given platform
 * listing of a theme is paid.
 *  - Explicit `platform.paid` wins (e.g. all-in-one Chrome = free).
 *  - Open-source themes: free everywhere EXCEPT LINE (LINE is always paid).
 *  - Non-open-source themes: paid everywhere.
 */
export function isPlatformPaid( product: Product, platformName?: string ): boolean {
  // Explicit per (theme + platform): every platform entry in product.data
  // carries its own `paid` flag. No derivation, any platform can be flipped
  // paid/free independently in the future.
  const entry = product?.platforms?.find( p => p.info?.name === platformName );
  return entry?.paid === true;
}

/**
 * CTA label for the currently-selected platform on the THEME page (the banner),
 * owner decisions 2026-09-20: "Add to cart" for a listing sold on the site (it
 * goes into the cart and the mini cart opens, the page stays); "Go to store" for
 * everything that links out to another store, paid (JetBrains premium, LINE) or
 * free (JetBrains, VS Code, Chrome...); "Download now" for a free file served
 * by the site outside the cart (Rhino, straight from GitHub; the free Ghostty,
 * iTerm2, macOS Terminal, Sublime Text and Slack listings carry a sku and go
 * through the cart, 2026-09-21); "Coming soon" when planned.
 */
export function platformCtaLabel( product: Product, platformName?: string, storeAvailability?: 'buy' | 'soon' | 'loading' ): string {
  const kind = platformCtaKind( product, platformName );
  // a store listing the store has not confirmed (no price yet, removed, store
  // unreachable) keeps its label and is disabled; the eyebrow already reads
  // "Coming soon" (owner, 2026-09-20)
  if ( kind === 'cart' ) return 'Add to cart';
  if ( kind === 'planned' ) return 'Coming soon';
  if ( kind === 'download' ) return 'Download now';
  // a free listing names the store it installs from (owner 2026-10-16, like the color pickers)
  if ( kind === 'get' ) {
    const store = FREE_STORE_NAMES[ platformName || '' ];
    if ( store ) return 'Install from ' + store;
  }
  // a paid listing on another store names it too (JetBrains plugins and the All In One bundle, LINE)
  if ( kind === 'external' ) {
    const store = PAID_STORE_NAMES[ platformName || '' ];
    if ( store ) return 'Buy on ' + store;
  }
  return 'Go to store';
}

/** The store a free listing installs from, by the site's platform name; the editors under Open VSX install from it too. */
const FREE_STORE_NAMES: Readonly<Record<string, string>> = {
  'JetBrains': 'JetBrains Marketplace',
  'VS Code': 'VS Code Marketplace',
  'Open VSX': 'Open VSX',
  'Cursor': 'Open VSX',
  'Antigravity': 'Open VSX',
  'VS Codium': 'Open VSX',
  'Chrome': 'Chrome Web Store',
  'Firefox': 'Firefox Add-ons',
  'Zed': 'Zed Extensions',
};

/** The store a paid listing on another store is bought on, by the site's platform name. */
const PAID_STORE_NAMES: Readonly<Record<string, string>> = {
  'JetBrains': 'JetBrains Marketplace',
  'Line': 'LINE Store',
};

/** "Free" for a free listing on another store (its CTA installs from there), else null: the offer beside the CTA reads "Free · Open source". */
export function freeStoreOfferLabel( product: Product | undefined, platformName?: string ): string | null {
  return platformCtaKind( product, platformName ) === 'get' && !isPlatformPlanned( product as Product, platformName ) ? 'Free' : null;
}

/**
 * Every label the banner CTA can wear (owner, 2026-09-20: the button is as wide
 * as the widest of them and never resizes when the platform changes; a pill
 * stacks them all in one cell and shows the current one).
 */
export const PLATFORM_CTA_LABELS: readonly string[] = [ 'Add to cart', 'Go to store', 'Download now', 'Coming soon' ];

/**
 * The labels one theme's CTA can wear: the four above plus "Install from <store>" for each free store
 * listing it has (owner 2026-10-16: a premium theme's button stays as wide as "Add to cart", only a
 * theme with an "Install from JetBrains Marketplace" grows to it). Cached per product.
 */
const CTA_LABELS_BY_PRODUCT = new WeakMap<Product, readonly string[]>();
export function platformCtaLabels( product: Product | undefined ): readonly string[] {
  if ( !product ) return PLATFORM_CTA_LABELS;
  let labels = CTA_LABELS_BY_PRODUCT.get( product );
  if ( !labels ) {
    const out = new Set( PLATFORM_CTA_LABELS );
    for ( const p of product.platforms || [] ) {
      const name = p.info?.name;
      const kind = platformCtaKind( product, name );
      if ( kind === 'external' && PAID_STORE_NAMES[ name || '' ] ) out.add( 'Buy on ' + PAID_STORE_NAMES[ name || '' ] );
      if ( kind !== 'get' ) continue;
      for ( const n of [ name, ...( p.info?.supportedPlatforms || [] ).map( ( sp: any ) => sp.name ) ] ) {
        const store = FREE_STORE_NAMES[ n || '' ];
        if ( store ) out.add( 'Install from ' + store );
      }
    }
    labels = Array.from( out );
    CTA_LABELS_BY_PRODUCT.set( product, labels );
  }
  return labels;
}

/** True when the selected platform is on the roadmap (status 'planned') and not
 *  yet shipped, used to mark the banner CTA inactive and flip the "Available on"
 *  eyebrow to "Coming soon". */
export function isPlatformPlanned( product: Product, platformName?: string ): boolean {
  const entry = product?.platforms?.find( p => p.info?.name === platformName );
  return entry?.status === 'planned';
}

/**
 * A theme reads as "free" when none of its non-Line platforms are paid (Line is
 * the only paid platform on otherwise-free themes). Replaces the removed
 * `openSource` field, monetization is now per-platform via `paid` flags.
 */
// The platforms whose listing is paid on every theme, the free ones too (LINE's store has no free
// themes; the Xcode themes are all sold at $2.99, 2026-10-13): they say nothing about the theme
// being premium, so isThemeFree leaves them out.
const PAID_ON_EVERY_THEME = new Set( [ 'Line', 'Xcode' ] );

export function isThemeFree( product: Product ): boolean {
  // Free when no platform is paid, apart from the ones paid on every theme (before Xcode, Line
  // was the only paid platform on otherwise-free themes). A product with no platforms reads as free.
  return !product?.platforms?.some( p => p.paid && !PAID_ON_EVERY_THEME.has( p.info?.name ) );
}

/**
 * The Rating stat label when a theme has no usable store score. Premium themes
 * are not sold individually (no per-theme marketplace listing to gather a
 * score), so they read as "N/A"; a free theme that simply has no rating yet is
 * "New". Used across the banner, expander, poster card and home strip so the
 * empty-rating wording stays consistent everywhere.
 */
/**
 * The licence model of one product's listing on one platform, or undefined
 * when that listing carries no Codigrate EULA / privacy policy of its own.
 * Drives /theme/license and /theme/privacy (?platform=&product=):
 *  - 'subscription': the All In One Themes bundle on JetBrains (monthly /
 *    annual, no fallback licence);
 *  - 'perpetual':    a premium theme's own standalone JetBrains plugin
 *    (one-time fee). Every premium theme resolves to this on JetBrains, not
 *    only the ones whose listing is already live: the owner needs the licence
 *    and privacy URLs while CREATING the listing on the Marketplace, before
 *    the site's link can switch over (Prague, 2026-09-15). Whether such a page
 *    is indexed is decided separately, by STANDALONE_PREMIUM_KEYS on the
 *    server and in the sitemap.
 * Anything else (free theme, unknown product, a platform without a paid
 * listing) resolves to nothing, and those pages go to not-found. A new paid platform (LINE, VS
 * Code, ...) is one more branch here. Platforms match on the site's platform
 * name or cssClass, so `platform=jetbrains` and `platform=JetBrains` both work.
 */
export type PaidListingKind = 'subscription' | 'perpetual';

const BUNDLE_KEY = 'all-in-one';

export function resolvePaidListing( product: Product | undefined, platformKey: string ): { kind: PaidListingKind, platformName: string } | undefined {
  const key = ( platformKey || '' ).toLowerCase();
  const platform = product?.platforms?.find( p =>
    p.info?.name?.toLowerCase() === key || p.info?.cssClass?.toLowerCase() === key
  );
  if ( !product || !platform || !platform.info || !isPlatformPaid( product, platform.info.name ) ) {
    return undefined;
  }
  if ( platform.info.name.toLowerCase() !== 'jetbrains' ) {
    return undefined; // no other paid platform carries a Codigrate EULA yet
  }
  if ( product.key === BUNDLE_KEY ) {
    return { kind: 'subscription', platformName: platform.info.name };
  }
  return { kind: 'perpetual', platformName: platform.info.name };
}

/** "All In One Themes" for the bundle, "Ocean Theme" for a theme. */
export function paidListingName( product: Product ): string {
  return product.key === BUNDLE_KEY ? 'All In One Themes' : `${ product.name } Theme`;
}

/**
 * JetBrains Marketplace's Bayesian rating (plugins.jetbrains.com/docs/marketplace/plugins-rating.html):
 * (sum of the votes + 2 x the Marketplace's mean vote) / (number of votes + 2), so a theme with
 * a couple of 5-star votes does not outrank one with many mixed ones. The mean vote is the
 * Marketplace's own (`meanRating` of /api/plugins/<id>/rating, 4.06431 on 2026-10-04); it moves
 * in the third decimal, so it is kept here as a constant.
 */
const PRIOR_VOTES = 2;
const PRIOR_MEAN = 4.06431;

/**
 * A theme's one rating over every place it is rated (owner, 2026-10-04): the votes on the
 * marketplaces (JetBrains, VS Code, Open VSX, Chrome, Firefox, plain averages + counts from the monitor)
 * and the approved ratings of buyers on codigrate.com, all put together and rated the JetBrains
 * way (above), out of 5. Rated on JetBrains only, it is exactly JetBrains' own number. A rating
 * whose count is unknown counts as one vote. 0 when nothing is rated.
 */
export function combinedRating(
  marketplaces: { rating?: number; ratingCount?: number }[] | null | undefined,
  store?: { count: number; average: number } | null,
): number {
  let sum = 0;
  let votes = 0;
  for ( const row of marketplaces || [] ) {
    const rating = Number( row?.rating ) || 0;
    if ( rating <= 0 ) {
      continue;
    }
    const count = marketplaceVotes( [ row ] );
    sum += rating * count;
    votes += count;
  }
  if ( store && store.count > 0 && store.average > 0 ) {
    sum += store.average * store.count;
    votes += store.count;
  }
  return votes > 0 ? ( sum + PRIOR_VOTES * PRIOR_MEAN ) / ( votes + PRIOR_VOTES ) : 0;
}

/** how many ratings the marketplaces hold (a rated row without a count counts once, as in combinedRating) */
export function marketplaceVotes( marketplaces: { rating?: number; ratingCount?: number }[] | null | undefined ): number {
  return ( marketplaces || [] ).reduce( ( votes, row ) =>
    ( Number( row?.rating ) || 0 ) > 0 ? votes + Math.max( 1, Number( row?.ratingCount ) || 0 ) : votes, 0 );
}

export function noRatingLabel( product: Product ): string {
  // A listing of its own (externalId) with no score yet is simply new, free or
  // paid alike; N/A is for a premium theme sold only through the bundle.
  const hasOwnListing = !!product?.platforms?.some( p => p.externalId );
  return isThemeFree( product ) || hasOwnListing ? 'New' : 'N/A';
}

/**
 * The canonical platform order, matching the home-page Platform Coverage matrix
 * (COVERAGE_PLATFORM_KEYS): IDE -> Text Editor -> Terminal -> CAD -> Browser ->
 * Communication. Keyed on the top-level platform's display name (lower-cased) so
 * every surface that lists a theme's platforms - theme cards, the theme page,
 * theme content and the themes-page filter - renders them in one consistent
 * order. Unknown names sort to the end.
 */
const PLATFORM_ORDER: string[] = [
  'jetbrains', 'xcode', 'vs code', 'open vsx', 'zed', 'sublime text',
  'macos terminal', 'iterm2', 'ghostty', 'claude code', 'codex', 'opencode', 'rhino', 'chrome', 'firefox', 'line', 'slack',
];

export function platformSortIndex( name?: string ): number {
  const i = PLATFORM_ORDER.indexOf( ( name || '' ).toLowerCase() );
  return i === -1 ? PLATFORM_ORDER.length : i;
}

/** Returns a copy of a theme's top-level platforms sorted into the coverage
 *  order. Sorting the TOP level (before any supportedPlatforms expansion) keeps
 *  Open VSX's editor variants grouped at Open VSX's position. Stable. The type
 *  parameter is unconstrained so callers passing a loosely-typed (`any`) list
 *  keep their element type rather than widening to the constraint. */
export function sortPlatformsByCoverage<T>( platforms: T[] ): T[] {
  return platforms
    .map( ( p, i ) => [ p, i ] as [ T, number ] )
    .sort( ( a, b ) => {
      const d = platformSortIndex( ( a[ 0 ] as any )?.info?.name )
        - platformSortIndex( ( b[ 0 ] as any )?.info?.name );
      return d !== 0 ? d : a[ 1 ] - b[ 1 ];
    } )
    .map( ( pair ) => pair[ 0 ] );
}

export interface Product {
  key: string;
  name: string;
  description: string;
  /**
   * One-line summary shown in the nav dropdown menus (kept short so each menu
   * item stays compact, a single line, clamped with an ellipsis). Falls back
   * to `description` when omitted.
   */
  shortDescription?: string;
  url: string;
  urlAlter?: string;
  urlDemo?: string;
  logo: string;
  video?: Video;
  demonstrationImg?: Image;
  demonstrationAlter?: Image;
  backgroundImages?: Image[];
  heroImage?: string;
  counters?: ProductCounter[];
  features?: PricingFeature[];
  pricingOptions?: PricingOption[];
  commandLines?: CommandLine[];
  steps?: StepTimeLineItem[];
  palette?: string[];
  imageLoaded?: boolean;
  techStack?: Image[];
  integrations?: any;
  platforms?: Platform[];
  redirectToUrl?: boolean;
  redirectToUrlAlter?: boolean;
  hasNpmPackage?: boolean;
  downloads?: string;
  rating?: number;
  comments?: any[];
  appearance?: string;
  designTokens?: any;
  discounts?: Discount[];
  category?: string;
  color?: string;
  tags?: string[];
  compactPalette?: string[];
  /**
   * Curated main-color buckets the theme reads as (e.g. ['Yellow'],
   * ['Teal', 'Blue']). Used by the themes page's Color filter so each
   * theme can be filed under labels that match human perception rather
   * than whatever the first two `compactPalette` hexes happen to bucket as.
   * If omitted, the filter falls back to auto-bucketing the first two
   * `compactPalette` via HSL.
   */
  mainColors?: string[];
  totalDownloads?: number;
  /**
   * Optional editorial / marketing label used by the screenshots page's
   * collection pass (e.g. "Editor's Choice", "Staff Pick", "Featured",
   * "Trending", "New Release"). Free-form short string per theme - it's
   * rendered uppercased in the badge value slot above a static
   * "FEATURED" label, so the badge reads e.g.
   *
   *     EDITOR'S CHOICE
   *     FEATURED
   *
   * Each theme can carry its own editorial spin without affecting any
   * other theme's marketplace screenshots.
   */
  editorialLabel?: string;
  /**
   * Release date as an ISO `YYYY-MM-DD` string (the theme's first commit in the
   * jetbrains-themes repo, our source of truth). Used for sorting / "new"
   * badges / freshness signals later - data only for now.
   */
  releaseDate?: string;
  /**
   * Marks this product as a bundle/collection of individual themes (the
   * All-in-One package). Its theme page shows a "This bundle includes" grid of
   * the themes it ships for the selected platform (see Platform.bundledThemes).
   */
  isBundle?: boolean;
}

export interface Discount {
  group: string;
  amount: string;
  /**
   * Platform names this discount is valid on (matched against the selected
   * platform's `name` or its `parentName`). Omit for a discount that applies
   * everywhere. e.g. the JetBrains Marketplace education programs are
   * `['JetBrains']` and don't show on Chrome.
   */
  platforms?: string[];
}

export interface ProductLine {
  key: string;
  name: string;
  description: string;
  color?: string;
  products: Product[];
}

export interface ProductCounter {
  name: string;
  value: string;
}

export interface CommandLine {
  command: string;
  type: CommandType;
  timeout: number;
}
